import { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  Card,
  CardContent,
  Grid
} from '@mui/material'
import {
  ExpandMore,
  HelpOutline,
  People,
  Business,
  Payment,
  Security,
  CloudQueue,
  Extension,
  Support
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

interface FAQ {
  question: string
  answer: string
  category: 'general' | 'pricing' | 'features' | 'security' | 'marketplace' | 'integrations' | 'support'
}

const faqs: FAQ[] = [
  // General Questions
  {
    category: 'general',
    question: 'What is this helpdesk system?',
    answer: 'Our helpdesk system is a multi-tenant support platform designed for MSPs and organisations managing multiple customer sites. It allows you to provide white-label support to your customers while maintaining complete control and visibility across all your sites from a single dashboard.'
  },
  {
    category: 'general',
    question: 'Who is this platform designed for?',
    answer: 'This platform is perfect for Managed Service Providers (MSPs), IT support companies, and organisations that manage support for multiple customer locations or departments. Whether you support libraries, retail chains, or corporate clients with multiple sites, our hierarchical structure keeps everything organised.'
  },
  {
    category: 'general',
    question: 'How does the multi-site structure work?',
    answer: 'Our system uses a three-tier hierarchy: Your Organisation (tenant) → Customer Organisations → Child Sites. For example, if you support "City Libraries", they might have child sites like "Central Library", "East Branch", etc. You see all tickets across all sites, while each site only sees their own tickets.'
  },
  {
    category: 'general',
    question: 'Can my customers create their own tickets?',
    answer: 'Yes! Each customer organisation and child site gets their own branded portal where end-users can create tickets, track progress, add comments, and upload attachments. They can access tickets via the web portal or email.'
  },
  {
    category: 'general',
    question: 'What user roles are available?',
    answer: 'The system supports multiple roles: Admin (full system access), Agent (ticket management and response), Customer (submit and track tickets), and Oversight User (cross-site visibility for IT managers). Each role has specific permissions to ensure proper access control.'
  },

  // Azure Marketplace Questions
  {
    category: 'marketplace',
    question: 'How does Azure Marketplace billing work?',
    answer: 'When you purchase through Azure Marketplace, billing is integrated directly into your existing Microsoft Azure subscription. The charges appear on your Azure invoice alongside your other Azure services, simplifying procurement and payment.'
  },
  {
    category: 'marketplace',
    question: 'Can I use my Azure consumption credits (MACC)?',
    answer: 'Yes! Azure Marketplace purchases are eligible for Microsoft Azure Consumption Commitment (MACC) credits, allowing you to use your existing Azure credits towards your helpdesk subscription.'
  },
  {
    category: 'marketplace',
    question: 'How do I purchase through Azure Marketplace vs. direct?',
    answer: 'Azure Marketplace purchases are processed through your Microsoft account and billed on your Azure invoice. Direct purchases use Stripe for payment processing with monthly billing. Both options provide the same features and support - choose the method that works best for your organisation.'
  },
  {
    category: 'marketplace',
    question: 'Is this compatible with Enterprise Agreements (EA) and CSP programs?',
    answer: 'Yes! Our Azure Marketplace offering is fully compatible with Microsoft Enterprise Agreements (EA) and Cloud Solution Provider (CSP) programs, making it easy to purchase through your existing Microsoft relationship and agreements.'
  },

  // Pricing Questions
  {
    category: 'pricing',
    question: 'How much does it cost?',
    answer: 'Simple, transparent pricing: £25 per agent per month. An "agent" is anyone on your team who responds to and manages tickets. There are no setup fees, no minimums, and no hidden costs. You can start with just 1 agent and scale up as you grow.'
  },
  {
    category: 'pricing',
    question: 'Is there a free trial?',
    answer: 'Yes! Every new account gets a 14-day free trial with full access to all features. No credit card required to start. You can invite your team, set up your customers, and test everything before committing.'
  },
  {
    category: 'pricing',
    question: 'What happens after the trial ends?',
    answer: 'Before your trial ends, you\'ll receive email reminders. To continue using the service, simply upgrade to a paid plan by selecting the number of agent seats you need. All your data remains safe - nothing is deleted.'
  },
  {
    category: 'pricing',
    question: 'Can I change the number of agents later?',
    answer: 'Absolutely! You can increase or decrease your agent count at any time through the billing settings. Changes take effect immediately, and billing is prorated.'
  },
  {
    category: 'pricing',
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit and debit cards (Visa, Mastercard, American Express) through our secure Stripe payment processing. Billing is monthly and automatic. You can also purchase through Azure Marketplace for integrated Microsoft billing.'
  },
  {
    category: 'pricing',
    question: 'Are there any user or storage limits?',
    answer: 'The number of agents (team members who respond to tickets) is based on your subscription plan. There are no limits on the number of customer users who can submit tickets. Storage quotas are generous and configurable based on your subscription tier. File attachments support up to 50MB per file.'
  },

  // Features Questions
  {
    category: 'features',
    question: 'What features are included?',
    answer: 'All plans include: Unlimited tickets, unlimited customer organisations and sites, email ticketing, web portal access, file attachments, internal notes, SLA tracking, escalation management, ticket merging, ticket forwarding, Kanban board view, real-time notifications, reporting and analytics, Microsoft Teams integration, Zapier integration, custom branding, and priority email support.'
  },
  {
    category: 'features',
    question: 'Can I manage multiple customer organisations?',
    answer: 'Yes! There\'s no limit to the number of customer organisations or child sites you can create. This makes it perfect for MSPs managing dozens or hundreds of customer locations from one central platform.'
  },
  {
    category: 'features',
    question: 'Does it support email ticketing?',
    answer: 'Yes! Configure a shared mailbox (Microsoft 365) and incoming emails automatically create tickets. Your team can reply to tickets via the portal or email, and customers receive updates in their inbox. Each organisation and child site can have unique email addresses for automatic routing.'
  },
  {
    category: 'features',
    question: 'What about reporting and analytics?',
    answer: 'Built-in reporting shows ticket volumes, resolution times, SLA compliance, agent performance, and trends over time. Filter by customer, site, priority, category, or date range to get the insights you need. Real-time dashboards provide at-a-glance visibility of your support operations.'
  },
  {
    category: 'features',
    question: 'Can I customise the branding?',
    answer: 'Each customer organisation can have custom branding including their own subdomain/URL. Your company name and branding appear throughout the platform. Email templates are also customizable per tenant.'
  },
  {
    category: 'features',
    question: 'What is SLA management?',
    answer: 'Our SLA management system tracks response and resolution deadlines for each ticket based on priority levels. You\'ll receive automatic alerts when tickets are at risk of breaching SLA, and comprehensive reporting shows your compliance rates. SLA deadlines are calculated automatically when tickets are created.'
  },
  {
    category: 'features',
    question: 'Can I merge duplicate tickets?',
    answer: 'Yes! Agents and admins can merge multiple duplicate or related tickets into a single ticket. All messages, attachments, and history from the merged tickets are preserved, and an audit trail tracks the merge operation.'
  },
  {
    category: 'features',
    question: 'Does it have a Kanban board view?',
    answer: 'Yes! The Kanban board provides a visual drag-and-drop interface for managing tickets across status columns (Open, In Progress, Resolved, Closed). This makes it easy to see your team\'s workload at a glance and quickly update ticket statuses.'
  },
  {
    category: 'features',
    question: 'What are group tickets and watchers?',
    answer: 'Group tickets are visible to all users within the same organisation or child site, making them perfect for company-wide announcements or known issues. Users can "watch" group tickets to receive notifications about updates even if they\'re not the ticket creator.'
  },
  {
    category: 'features',
    question: 'What are private tickets?',
    answer: 'Private tickets are only visible to the requester and your support agents. This feature can be enabled per organisation and is essential for handling sensitive information in compliance with HIPAA, GDPR, or other privacy regulations.'
  },
  {
    category: 'features',
    question: 'Can agents leave internal notes?',
    answer: 'Yes! Agents can add internal notes to tickets that are only visible to other agents and admins, never to customers. This is perfect for documenting troubleshooting steps, escalation notes, or coordination between team members.'
  },
  {
    category: 'features',
    question: 'Can I forward tickets to external parties?',
    answer: 'Yes! Tickets can be forwarded to external email addresses with all attachments included. The system maintains a complete audit trail of forwarded tickets, including who forwarded them and when.'
  },
  {
    category: 'features',
    question: 'Do you offer real-time notifications?',
    answer: 'Yes! The platform uses SignalR for real-time updates without page refreshes. You\'ll see live notifications for new tickets, status changes, assignments, and new messages. Typing indicators show when agents are composing responses, and you can see who else is viewing a ticket.'
  },
  {
    category: 'features',
    question: 'How does the escalation system work?',
    answer: 'The escalation system supports multi-level escalation paths (up to 3 levels) with automatic escalation based on SLA thresholds, priority, or manual triggers. Each escalation is tracked with a complete history, and you can define escalation paths per category with specific users or roles at each level.'
  },
  {
    category: 'features',
    question: 'Can oversight users see tickets across multiple sites?',
    answer: 'Yes! Oversight users can be assigned to specific child sites within an organisation, giving them cross-site visibility for their assigned locations. This is perfect for IT managers or regional supervisors who need to monitor multiple sites without full admin access.'
  },

  // Integrations Questions
  {
    category: 'integrations',
    question: 'What Microsoft 365 integrations are available?',
    answer: 'The system integrates with Microsoft 365 shared mailboxes for automatic email-to-ticket conversion. Emails sent to your configured support email automatically create tickets with smart routing based on recipient address. Reply tracking ensures email responses become ticket comments automatically.'
  },
  {
    category: 'integrations',
    question: 'Can I integrate with Microsoft Teams?',
    answer: 'Yes! Get real-time notifications in your Microsoft Teams channels when tickets are created, assigned, updated, or resolved. Notifications include rich cards with ticket details and quick links. Setup takes just a few minutes using webhooks.'
  },
  {
    category: 'integrations',
    question: 'What Zapier integrations are available?',
    answer: 'Our Zapier integration allows you to connect with 5,000+ apps including Slack, Salesforce, Jira, and more. Webhook events are triggered for all ticket lifecycle changes (created, assigned, updated, resolved), enabling custom workflows tailored to your business needs.'
  },
  {
    category: 'integrations',
    question: 'Is there an API for custom integrations?',
    answer: 'Yes! The platform provides a comprehensive REST API with JWT authentication for custom integrations. API requests support HMAC-SHA256 signing for sensitive operations, and per-user API keys are available for webhook and integration authentication.'
  },

  // Security Questions
  {
    category: 'security',
    question: 'Is my data secure?',
    answer: 'Yes! We use enterprise-grade security including AES-256 encryption at rest, TLS 1.2+ encryption in transit, regular automated backups, secure authentication with MFA support, role-based access control, and hosting on Microsoft Azure UK data centres. We take security very seriously.'
  },
  {
    category: 'security',
    question: 'Where is data stored?',
    answer: 'All data is stored in Microsoft Azure data centres in the United Kingdom (UK South region), ensuring compliance with UK and EU data protection regulations including GDPR. This also meets UK Government OFFICIAL classification standards and NHS Data Security requirements.'
  },
  {
    category: 'security',
    question: 'Who can see what data?',
    answer: 'The system uses strict role-based access control with database-level row-level security (RLS). Customers only see their own tickets, child sites only see their site\'s tickets, and agents/admins see tickets based on assigned permissions. Complete tenant isolation prevents any cross-tenant data access. Private tickets are only visible to the requester and support agents.'
  },
  {
    category: 'security',
    question: 'What authentication methods are supported?',
    answer: 'The platform supports JWT token authentication with secure password requirements (15-character minimum, complexity rules, banned password list, history tracking). Multi-Factor Authentication (MFA) is available using TOTP (compatible with Google Authenticator, Microsoft Authenticator, Authy) with recovery codes for account recovery.'
  },
  {
    category: 'security',
    question: 'Do you offer Multi-Factor Authentication (MFA)?',
    answer: 'Yes! MFA is mandatory for all admin accounts and available for all users. We support TOTP-based authentication compatible with all major authenticator apps. Each user receives 8 one-time recovery codes for account recovery, and MFA secrets are encrypted using RSA-2048 via Azure Key Vault.'
  },
  {
    category: 'security',
    question: 'What compliance certifications do you have?',
    answer: 'We have completed a comprehensive security audit covering OWASP Top 10, and we fully implement NIST SP 800-63-4 password guidelines. Our security controls are aligned with UK Government Cyber Essentials requirements, and we\'re suitable for handling UK Government OFFICIAL classified data. We\'re built to meet NHS Data Security and Protection Toolkit standards. Our architecture is designed to support independent penetration testing by your own security team if required.'
  },
  {
    category: 'security',
    question: 'Has the platform been security tested?',
    answer: 'Yes. We have completed a comprehensive security audit covering OWASP Top 10 vulnerabilities, authentication security, input validation, and infrastructure hardening — with all findings remediated. We welcome customers who wish to conduct their own independent penetration testing and are confident in our security posture.'
  },
  {
    category: 'security',
    question: 'What\'s your uptime SLA?',
    answer: 'We maintain a 99.9% uptime SLA backed by Microsoft Azure\'s infrastructure. Our platform is hosted on Azure App Services with automatic failover, geo-redundant storage, and built-in monitoring. Azure Front Door with WAF provides DDoS protection and load balancing.'
  },
  {
    category: 'security',
    question: 'What backup and disaster recovery do you provide?',
    answer: 'Daily automated backups with 90-day retention (configurable), geo-redundant storage for database and file backups, automatic failover for critical services, monthly restore verification testing, Recovery Time Objective (RTO) of less than 4 hours, and Recovery Point Objective (RPO) of less than 1 hour. We maintain a documented and tested incident response plan.'
  },
  {
    category: 'security',
    question: 'Do you have security certifications?',
    answer: 'Our security controls are aligned with UK Government Cyber Essentials baseline security requirements. We\'re suitable for handling UK Government OFFICIAL classified data and built to meet NHS Data Security and Protection Toolkit standards. We are actively working toward formal ISO 27001, SOC 2 Type II, and HIPAA compliance certifications.'
  },
  {
    category: 'security',
    question: 'Can I export my data?',
    answer: 'Yes! You can export tickets, reports, and customer data at any time through the platform. For complete data exports (GDPR right to access), we can provide a full export of all your data in standard formats. This ensures you always have access to your information.'
  },
  {
    category: 'security',
    question: 'What security headers and protections are in place?',
    answer: 'We implement comprehensive security headers including HSTS with preload, strict Content-Security-Policy, X-Content-Type-Options (nosniff), X-Frame-Options (DENY for clickjacking protection), and Referrer-Policy. Additional protections include rate limiting, geo-blocking capabilities, malicious user-agent blocking, HTTPS enforcement, and Azure Front Door with Web Application Firewall (WAF) for DDoS and OWASP Top 10 protection.'
  },

  // Support Questions
  {
    category: 'support',
    question: 'What support options do you offer?',
    answer: 'All customers receive priority email support with response within one business day. We provide comprehensive documentation, video tutorials, and in-app help. For enterprise customers, we can arrange dedicated onboarding sessions and ongoing account management.'
  },
  {
    category: 'support',
    question: 'What happens to my data if I cancel?',
    answer: 'When you cancel, you enter a 30-day grace period during which you can reactivate your account and recover all data. During this period, your data remains intact but the account is suspended. After 30 days, data is permanently deleted with an immutable deletion audit log. You can request a full data export before or during the grace period.'
  },
  {
    category: 'support',
    question: 'Can I migrate from another helpdesk system?',
    answer: 'Yes! We can assist with data migration from other helpdesk systems. Contact our support team to discuss your current platform and migration requirements. We\'ll work with you to ensure a smooth transition with minimal disruption to your operations.'
  },
  {
    category: 'support',
    question: 'How do I get started?',
    answer: 'Simply sign up for a free 14-day trial - no credit card required. You\'ll immediately get access to the platform where you can create your first organisation, invite your team, configure email integration, and start managing tickets. Our onboarding guide walks you through the setup process step-by-step.'
  },
  {
    category: 'support',
    question: 'Do you offer training or onboarding?',
    answer: 'Yes! We provide comprehensive documentation, video tutorials, and help articles for self-service learning. For larger deployments or enterprise customers, we can arrange personalized onboarding sessions to ensure your team is fully trained and confident using the platform.'
  }
]

function FAQPage() {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState<string | false>(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  const categories = [
    { id: 'all', label: 'All Questions', icon: <HelpOutline /> },
    { id: 'general', label: 'General', icon: <Business /> },
    { id: 'marketplace', label: 'Azure Marketplace', icon: <CloudQueue /> },
    { id: 'pricing', label: 'Pricing', icon: <Payment /> },
    { id: 'features', label: 'Features', icon: <People /> },
    { id: 'integrations', label: 'Integrations', icon: <Extension /> },
    { id: 'security', label: 'Security', icon: <Security /> },
    { id: 'support', label: 'Support', icon: <Support /> }
  ]

  const filteredFaqs = selectedCategory === 'all'
    ? faqs
    : faqs.filter(faq => faq.category === selectedCategory)

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Frequently Asked Questions
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Everything you need to know about our helpdesk platform
          </Typography>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/')}
          >
            Back to Home
          </Button>
        </Box>

        {/* Category Filter */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {categories.map((category) => (
            <Grid item xs={6} sm={4} md={3} key={category.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  bgcolor: selectedCategory === category.id ? 'primary.main' : 'background.paper',
                  color: selectedCategory === category.id ? 'white' : 'text.primary',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3
                  }
                }}
                onClick={() => setSelectedCategory(category.id)}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ mb: 1 }}>{category.icon}</Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {category.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* FAQ Accordion */}
        <Box>
          {filteredFaqs.map((faq, index) => (
            <Accordion
              key={index}
              expanded={expanded === `panel${index}`}
              onChange={handleChange(`panel${index}`)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
              >
                <Typography sx={{ fontWeight: 600 }}>{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mt: 8, p: 4, bgcolor: 'primary.main', color: 'white', borderRadius: 2 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
            Still have questions?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            We're here to help! Start your free 14-day trial or get in touch with our team.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
              onClick={() => navigate('/register')}
            >
              Start Free Trial
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              onClick={() => window.location.href = 'mailto:support@taylindigital.com'}
            >
              Contact Support
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}

export default FAQPage
